// What the HUD knows, and the only path by which it learns it.
//
// The interface is driven from the network, and the network is a demultiplexer
// that fires callbacks at 2 Hz. Wiring those callbacks straight into React state
// would re-render the whole HUD on every DELTA — which is the one thing §15 says
// not to do, because "the UI shares a frame budget with a WebGL canvas running
// tracer animation".
//
// So: one external store with one immutable snapshot, read through
// `useSyncExternalStore`. Every mutator swaps the snapshot and notifies once; a
// mutator that would not change anything notifies nobody, which is what keeps a
// quiet tick free.
//
// Nothing here imports React. The store is a plain object so it can be driven in
// a test, and so main.tsx can feed it before anything is mounted.

import type { ConnectionState } from '../net/connection';
import type { BankState, Outcome, Refusal } from '../net/clicks';
import type { Verdict } from '../wire/core';
import { DefaultVolumes, type Bus } from '../audio/mixer';

export interface HoverInfo {
  pixel: number;
  x: number;
  y: number;
  owner: number;
  hp: number;
  flags: number;
}

export interface FeedEntry {
  /** Monotonic, so React keys are stable and a repeated line is still two rows. */
  id: number;
  kind: number;
  country: number;
  other: number;
  pixel: number;
  hasPixel: boolean;
  text: string;
  at: number;
}

export interface HudSnapshot {
  link: ConnectionState;
  tick: number;
  /** Frames were missed; the board is discarded and a keyframe is awaited (§3). */
  gapped: boolean;
  country: number;
  countryLocked: boolean;
  bank: BankState;
  bankKnown: boolean;
  lastOutcome: { outcome: Outcome; verdict: Verdict | null; at: number } | null;
  /** A click the client refused before sending. Advisory, and it never left the tab. */
  refusal: { reason: Refusal; at: number } | null;
  hover: HoverInfo | null;
  feed: readonly FeedEntry[];
  reject: { code: number; reason: string } | null;
  reducedMotion: boolean;
  audioLive: boolean;
  volumes: Record<Bus, number>;
  /** Live arc count, for the corner readout. It is also the cheapest proof the pass is running. */
  arcs: number;
}

/** How many feed lines are kept. §16: audio is never the only channel, so this is the record. */
export const FeedLimit = 40;

const EMPTY: HudSnapshot = {
  link: 'idle',
  tick: 0,
  gapped: false,
  country: 0,
  countryLocked: false,
  bank: { banked: 0, capacity: 0, refillSeconds: 0 },
  bankKnown: false,
  lastOutcome: null,
  refusal: null,
  hover: null,
  feed: [],
  reject: null,
  reducedMotion: false,
  audioLive: false,
  volumes: { ...DefaultVolumes },
  arcs: 0,
};

export class HudStore {
  private snap: HudSnapshot = EMPTY;
  private readonly listeners = new Set<() => void>();
  private feedSeq = 0;

  snapshot = (): HudSnapshot => this.snap;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** The one mutator. Everything else is a thin wrapper so call sites read as English. */
  patch(next: Partial<HudSnapshot>): void {
    let changed = false;
    for (const k of Object.keys(next) as (keyof HudSnapshot)[]) {
      if (!Object.is(this.snap[k], next[k])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.snap = { ...this.snap, ...next };
    for (const fn of this.listeners) fn();
  }

  setLink(link: ConnectionState): void {
    this.patch({ link });
  }

  setTick(tick: number): void {
    this.patch({ tick, gapped: false });
  }

  setGap(): void {
    this.patch({ gapped: true });
  }

  setWelcome(country: number, locked: boolean): void {
    this.patch({ country, countryLocked: locked, reject: null });
  }

  setBank(bank: BankState, known: boolean): void {
    const cur = this.snap.bank;
    if (
      known === this.snap.bankKnown &&
      cur.banked === bank.banked &&
      cur.capacity === bank.capacity &&
      cur.refillSeconds === bank.refillSeconds
    ) {
      return;
    }
    this.patch({ bank, bankKnown: known });
  }

  setOutcome(outcome: Outcome, verdict: Verdict | null, at: number): void {
    this.patch({ lastOutcome: { outcome, verdict, at }, refusal: null });
  }

  setRefusal(reason: Refusal, at: number): void {
    this.patch({ refusal: { reason, at } });
  }

  /**
   * The hovered pixel.
   *
   * Compared field by field rather than by identity, because this fires on every
   * mouse move and a fresh object every time would re-render the HUD at the
   * pointer's sample rate — which on a 1000 Hz mouse is faster than the display.
   */
  setHover(h: HoverInfo | null): void {
    const cur = this.snap.hover;
    if (h === null && cur === null) return;
    if (h && cur && h.pixel === cur.pixel && h.owner === cur.owner && h.hp === cur.hp && h.flags === cur.flags) {
      return;
    }
    this.patch({ hover: h });
  }

  pushFeed(lines: readonly Omit<FeedEntry, 'id' | 'at'>[], at: number): void {
    if (lines.length === 0) return;
    const next = this.snap.feed.slice();
    for (const l of lines) next.unshift({ ...l, id: ++this.feedSeq, at });
    next.length = Math.min(next.length, FeedLimit);
    this.patch({ feed: next });
  }

  setReject(code: number, reason: string): void {
    this.patch({ reject: { code, reason } });
  }

  setReducedMotion(reducedMotion: boolean): void {
    this.patch({ reducedMotion });
  }

  setAudio(audioLive: boolean, volumes: Record<Bus, number>): void {
    this.patch({ audioLive, volumes });
  }

  setArcs(arcs: number): void {
    this.patch({ arcs });
  }
}
